import { IntentPattern } from './intent-service.js';
import { FrontmatterMetadata } from '../types.js';
import { InterpretedIntent } from './intent-service.js';
import { logger } from '../utils/logger.js';
import { ValidationResult } from '../types.js';
import { LLMail } from '../LLMail.js';
import { ResolvedConfig } from '../utils/config.js';

/**
 * Context object passed to plugin commands during execution
 */
export interface CommandContext {
  llmail: LLMail;           // Core llmail instance
  logger: typeof logger;     // llmail's logger
  config: ResolvedConfig;   // Current config
  cwd: string;             // Working directory
  pluginService: PluginService; // Access to plugin service for configuration
}

/**
 * Describes a command that a plugin may register to extend the llmail CLI.
 */
export interface PluginCommand {
  name: string;
  description: string;
  subcommands?: PluginCommand[];
  options?: Array<{
    name: string;
    description?: string;
    type?: 'boolean' | 'string' | 'number';
    required?: boolean;
    default?: any;
  }>;
  
  /** Execute the command with arguments and context */
  execute: (args: Record<string, any>, context: CommandContext) => Promise<void>;
  
  /** Optional hook that runs before command execution. Can modify args. */
  beforeExecute?: (args: Record<string, any>) => Promise<void | Record<string, any>>;
  
  /** Optional hook that runs after successful command execution */
  afterExecute?: (result: any) => Promise<void>;
  
  /** Optional hook that runs when an error occurs. Can transform the error. */
  onError?: (error: Error, args: Record<string, any>) => Promise<void | Error>;
}

/**
 * Interface that all plugins must implement
 */
export interface Plugin {
  /** Unique identifier for the plugin */
  name: string;

  /** Plugin priority - higher numbers run first (default: 0) */
  priority?: number;

  /** Default configuration for this plugin */
  defaultConfig?: Record<string, any>;

  /** Optional function to validate plugin configuration */
  validateConfig?: (config: Record<string, any>) => ValidationResult;

  /** Register new or override existing intent patterns */
  registerIntents?: (existingPatterns: IntentPattern[]) => IntentPattern[];
  
  /** Register additional valid types */
  registerTypes?: () => string[];
  
  /** Register additional valid active states */
  registerActiveStates?: () => string[];
  
  /** Register additional valid inactive reasons */
  registerInactiveReasons?: () => string[];
  
  /** Custom metadata validation hook */
  validateMetadata?: (metadata: FrontmatterMetadata) => ValidationResult;
  
  /** Post-processing hook for interpreted intents */
  postInterpretIntent?: (intent: InterpretedIntent) => InterpretedIntent;

  /** Optional: Register additional CLI commands for this plugin */
  registerCommands?: () => PluginCommand[];
}

export class PluginService {
  private plugins: Plugin[] = [];
  private registeredTypes: Set<string> = new Set();
  private registeredActiveStates: Set<string> = new Set();
  private registeredInactiveReasons: Set<string> = new Set();
  private pluginConfigs: Map<string, any> = new Map();
  private commands: PluginCommand[] = [];

  /**
   * Register a new plugin
   */
  registerPlugin(plugin: Plugin): void {
    logger.debug(`Registering plugin: ${plugin.name}`);

    // Check if plugin is already registered
    if (this.plugins.some(p => p.name === plugin.name)) {
      throw new Error(
        `Plugin ${plugin.name} is already registered. ` +
        `This usually means the plugin is being registered at the wrong level. ` +
        `Plugins should be registered once at the run level, not per handled object.`
      );
    }
    
    // Register commands
    if (plugin.registerCommands) {
      logger.debug(`Registering commands for plugin ${plugin.name}`);
      try {
        const commands = plugin.registerCommands();
        logger.debug(`Plugin ${plugin.name} registered commands:`, commands);
        this.commands.push(...commands);
      } catch (error) {
        logger.error(`Error registering commands for plugin ${plugin.name}:`, error);
      }
    }

    // Register types
    if (plugin.registerTypes) {
      logger.debug(`Registering types for plugin ${plugin.name}`);
      try {
        const types = plugin.registerTypes();
        logger.debug(`Plugin ${plugin.name} registered types:`, types);
        types.forEach(type => this.registeredTypes.add(type));
      } catch (error) {
        logger.error(`Error registering types for plugin ${plugin.name}:`, error);
      }
    }

    // Register active states
    if (plugin.registerActiveStates) {
      logger.debug(`Registering active states for plugin ${plugin.name}`);
      try {
        const states = plugin.registerActiveStates();
        logger.debug(`Plugin ${plugin.name} registered active states:`, states);
        states.forEach(state => this.registeredActiveStates.add(state));
      } catch (error) {
        logger.error(`Error registering active states for plugin ${plugin.name}:`, error);
      }
    }

    // Register inactive reasons
    if (plugin.registerInactiveReasons) {
      logger.debug(`Registering inactive reasons for plugin ${plugin.name}`);
      try {
        const reasons = plugin.registerInactiveReasons();
        logger.debug(`Plugin ${plugin.name} registered inactive reasons:`, reasons);
        reasons.forEach(reason => this.registeredInactiveReasons.add(reason));
      } catch (error) {
        logger.error(`Error registering inactive reasons for plugin ${plugin.name}:`, error);
      }
    }

    // Initialize plugin config with defaults if provided
    if (plugin.defaultConfig) {
      logger.debug(`Initializing config for plugin ${plugin.name}`);
      this.pluginConfigs.set(plugin.name, { ...plugin.defaultConfig });
    }

    this.plugins.push(plugin);
    
    // Sort plugins by priority (highest first)
    this.plugins.sort((a, b) => (b.priority || 0) - (a.priority || 0));

    logger.info(`Registered plugin: ${plugin.name}`);
  }

  /**
   * Get configuration for a specific plugin
   */
  getPluginConfig(pluginName: string): any {
    return this.pluginConfigs.get(pluginName);
  }

  /**
   * Set configuration for a specific plugin
   */
  setPluginConfig(pluginName: string, config: any): void {
    const plugin = this.plugins.find(p => p.name === pluginName);
    if (!plugin) {
      throw new Error(`Plugin "${pluginName}" not found`);
    }

    // Validate config if plugin has a validator
    if (plugin.validateConfig) {
      const result = plugin.validateConfig(config);
      if (!result.valid) {
        throw new Error(`Invalid config for plugin ${pluginName}: ${result.errors?.join(', ')}`);
      }
    }

    this.pluginConfigs.set(pluginName, config);
  }

  /**
   * Get all registered plugins
   */
  getPlugins(): Plugin[] {
    return [...this.plugins];
  }

  /**
   * Apply all plugin intent patterns to the existing patterns
   */
  applyPluginIntents(existingPatterns: IntentPattern[]): IntentPattern[] {
    let patterns = [...existingPatterns];

    for (const plugin of this.plugins) {
      try {
        if (plugin.registerIntents) {
          patterns = plugin.registerIntents(patterns);
        }
      } catch (error) {
        logger.error(`Error applying intents from plugin "${plugin.name}":`, error);
      }
    }

    return patterns;
  }

  /**
   * Get all valid types (including plugin-provided ones)
   */
  getValidTypes(): string[] {
    // Start with core types
    const types = new Set(this.registeredTypes);
    
    // Add plugin-provided types
    for (const plugin of this.plugins) {
      try {
        if (plugin.registerTypes) {
          const pluginTypes = plugin.registerTypes();
          pluginTypes.forEach(type => types.add(type));
        }
      } catch (error) {
        logger.error(`Error getting types from plugin "${plugin.name}":`, error);
      }
    }

    // Always ensure 'issue' is included
    types.add('issue');

    return Array.from(types);
  }

  /**
   * Get all valid active states (including plugin-provided ones)
   */
  getValidActiveStates(): string[] {
    const states = new Set(this.registeredActiveStates);
    
    for (const plugin of this.plugins) {
      try {
        if (plugin.registerActiveStates) {
          plugin.registerActiveStates().forEach(state => states.add(state));
        }
      } catch (error) {
        logger.error(`Error getting active states from plugin "${plugin.name}":`, error);
      }
    }

    return Array.from(states);
  }

  /**
   * Get all valid inactive reasons (including plugin-provided ones)
   */
  getValidInactiveReasons(): string[] {
    const reasons = new Set(this.registeredInactiveReasons);
    
    for (const plugin of this.plugins) {
      try {
        if (plugin.registerInactiveReasons) {
          plugin.registerInactiveReasons().forEach(reason => reasons.add(reason));
        }
      } catch (error) {
        logger.error(`Error getting inactive reasons from plugin "${plugin.name}":`, error);
      }
    }

    return Array.from(reasons);
  }

  /**
   * Get all valid states (both active states and inactive reasons)
   */
  getValidStates(): string[] {
    return [
      ...this.getValidActiveStates(),
      ...this.getValidInactiveReasons()
    ];
  }

  /**
   * Run validation hooks from all plugins
   */
  validateMetadata(metadata: FrontmatterMetadata): ValidationResult {
    const result: ValidationResult = {
      valid: true,
      errors: [],
      warnings: []
    };

    for (const plugin of this.plugins) {
      try {
        if (plugin.validateMetadata) {
          const pluginResult = plugin.validateMetadata(metadata);
          
          // Merge warnings
          if (pluginResult.warnings) {
            result.warnings.push(...pluginResult.warnings);
          }

          // If plugin validation fails, merge errors and mark as invalid
          if (!pluginResult.valid) {
            result.valid = false;
            if (pluginResult.errors) {
              result.errors.push(...pluginResult.errors);
            }
          }
        }
      } catch (error: any) {
        logger.error(`Error validating metadata in plugin "${plugin.name}":`, error);
        result.warnings.push(`Plugin ${plugin.name} validation error: ${error.message}`);
      }
    }

    return result;
  }

  /**
   * Run post-processing hooks from all plugins
   */
  processIntent(intent: InterpretedIntent): InterpretedIntent {
    let processedIntent = { ...intent };

    for (const plugin of this.plugins) {
      try {
        if (plugin.postInterpretIntent) {
          processedIntent = plugin.postInterpretIntent(processedIntent);
        }
      } catch (error) {
        logger.error(`Error processing intent in plugin "${plugin.name}":`, error);
      }
    }

    return processedIntent;
  }

  /**
   * Register core types that plugins can extend
   */
  registerCoreTypes(types: string[]): void {
    // Add each core type to existing types
    types.forEach(type => this.registeredTypes.add(type));
  }

  /**
   * Register core active states that plugins can extend
   */
  registerCoreActiveStates(states: string[]): void {
    states.forEach(state => this.registeredActiveStates.add(state));
  }

  /**
   * Register core inactive reasons that plugins can extend
   */
  registerCoreInactiveReasons(reasons: string[]): void {
    reasons.forEach(reason => this.registeredInactiveReasons.add(reason));
  }

  /**
   * Collects all plugin commands from every plugin in the order of their priority.
   */
  getAllCommands(): PluginCommand[] {
    logger.debug('Getting all commands:', this.commands);
    return this.commands;
  }
} 